import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const PropertySpecifications = ({ specifications, floorPlan, amenities }) => {
  const [activeTab, setActiveTab] = useState('specifications');

  const tabs = [
    { id: 'specifications', label: 'Specifications', icon: 'FileText' },
    { id: 'floorplan', label: 'Floor Plan', icon: 'Layout' },
    { id: 'amenities', label: 'Amenities', icon: 'Star' }
  ];

  const getAmenityIcon = (amenity) => {
    const iconMap = {
      'Swimming Pool': 'Waves',
      'Gym': 'Dumbbell',
      'Parking': 'Car',
      'Security': 'Shield',
      'Garden': 'Trees',
      'Elevator': 'ArrowUp',
      'Power Backup': 'Zap',
      'Water Supply': 'Droplets',
      'Club House': 'Building',
      'Children Play Area': 'Baby',
      'Jogging Track': 'Activity',
      'CCTV': 'Camera',
      'Intercom': 'Phone',
      'Fire Safety': 'Flame',
      'Waste Management': 'Trash2',
      'Maintenance': 'Wrench'
    };
    return iconMap[amenity] || 'Check';
  };

  return (
    <div className="bg-surface rounded-lg shadow-property-card">
      {/* Tab Navigation */}
      <div className="border-b border-border">
        <div className="flex">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-6 py-4 text-sm font-medium transition-smooth ${
                activeTab === tab.id
                  ? 'text-primary border-b-2 border-primary' :'text-text-secondary hover:text-text-primary'
              }`}
            >
              <Icon name={tab.icon} size={16} />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="p-6">
        {activeTab === 'specifications' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {Object.entries(specifications).map(([category, items]) => (
              <div key={category} className="space-y-3">
                <h3 className="text-lg font-semibold text-text-primary capitalize">
                  {category.replace(/([A-Z])/g, ' $1').trim()}
                </h3>
                <div className="space-y-2">
                  {Object.entries(items).map(([key, value]) => (
                    <div key={key} className="flex justify-between items-center py-2 border-b border-border/50">
                      <span className="text-text-secondary capitalize">
                        {key.replace(/([A-Z])/g, ' $1').trim()}
                      </span>
                      <span className="text-text-primary font-medium">{value}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'floorplan' && (
          <div className="space-y-4">
            <div className="text-center">
              <h3 className="text-lg font-semibold text-text-primary mb-4">
                Floor Plan Layout
              </h3>
              <div className="max-w-2xl mx-auto">
                <Image
                  src={floorPlan}
                  alt="Property floor plan"
                  className="w-full h-auto rounded-lg border border-border"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
              <div className="text-center p-4 bg-muted rounded-lg">
                <Icon name="Home" size={24} className="mx-auto mb-2 text-primary" />
                <div className="text-sm text-text-secondary">Total Rooms</div>
                <div className="font-semibold text-text-primary">4</div>
              </div>
              <div className="text-center p-4 bg-muted rounded-lg">
                <Icon name="Bath" size={24} className="mx-auto mb-2 text-primary" />
                <div className="text-sm text-text-secondary">Bathrooms</div>
                <div className="font-semibold text-text-primary">2</div>
              </div>
              <div className="text-center p-4 bg-muted rounded-lg">
                <Icon name="Square" size={24} className="mx-auto mb-2 text-primary" />
                <div className="text-sm text-text-secondary">Balconies</div>
                <div className="font-semibold text-text-primary">2</div>
              </div>
              <div className="text-center p-4 bg-muted rounded-lg">
                <Icon name="Car" size={24} className="mx-auto mb-2 text-primary" />
                <div className="text-sm text-text-secondary">Parking</div>
                <div className="font-semibold text-text-primary">1</div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'amenities' && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-text-primary mb-4">
                Building Amenities
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {amenities.building.map((amenity, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                    <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                      <Icon name={getAmenityIcon(amenity)} size={16} className="text-primary" />
                    </div>
                    <span className="text-text-primary font-medium">{amenity}</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-text-primary mb-4">
                Nearby Facilities
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {amenities.nearby.map((facility, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                    <div className="w-8 h-8 bg-secondary/10 rounded-full flex items-center justify-center">
                      <Icon name={getAmenityIcon(facility)} size={16} className="text-secondary" />
                    </div>
                    <span className="text-text-primary font-medium">{facility}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PropertySpecifications;